import { InputOptions } from '@components/NextForms/TextInput';
import { getDecodeResult, type MobxZodField } from '@monoid-dev/mobx-zod-form';
import { type ReactForm } from '@monoid-dev/mobx-zod-form-react/lib/ReactForm';
import { type FormikContextType, useField } from 'formik';
import { action, computed, makeObservable, observable } from 'mobx';
import { type ZodTypeAny } from 'zod';

export interface TagSearchField {
  label: string;
  inputOptions?: InputOptions;
}

export interface TagSearchInputControl {
  field: TagSearchField;
}

export type TagSearchInputState = 'START' | 'SELECT_FIELD' | 'INPUT';

export class TagSearchInputStore {
  @observable
  searchValue: any = '';

  @observable
  focused = false;

  @observable
  controls: Record<string, TagSearchInputControl>;

  @observable
  currentField?: string;

  @observable
  selectedAutoCompleteTag: string = '';

  @observable
  state: TagSearchInputState = 'START';

  constructor(public formControl: TagSearchInputFormControl) {
    this.controls = {};

    for (const [name, field] of Object.entries(formControl.fields)) {
      if (this.formControl.getFieldValue(name)) {
        this.controls[name] = {
          field,
        };
      }
    }

    makeObservable(this);
  }

  get fields() {
    return this.formControl.fields;
  }

  @action
  setFocused(v: boolean) {
    this.focused = v;
    if (v) {
      this.setSelectedAutoCompleteTag(Object.keys(this.fields)[0]);
    }
  }

  @action
  setSearchText(v: any) {
    this.searchValue = v;
  }

  @computed
  get hasInput() {
    return (
      (this.searchValue && this.searchValue.length > 0) || Object.keys(this.controls).length > 0
    );
  }

  @action
  setSelectedAutoCompleteTag(v: string) {
    this.selectedAutoCompleteTag = v;
  }

  @action
  clickInput() {
    this.setSelectedAutoCompleteTag(Object.keys(this.fields)[0]);
    this.state = 'SELECT_FIELD';
  }

  @action
  clickAway() {
    this.maybeAddSearchTag();
    this.state = 'START';
  }

  @action
  maybeAddSearchTag() {
    const currentText = this.searchValue.trim();

    if (!this.currentField) return;
    if (currentText === '') return;

    // So that the key will be at the end for Object.keys
    delete this.controls[this.currentField];

    this.controls[this.currentField] = {
      field: this.fields[this.currentField],
    };

    this.formControl.setFieldValue(this.currentField, currentText);

    this.searchValue = '';
  }

  @action
  startTag(field: string) {
    this.state = 'INPUT';
    this.currentField = field;
  }

  @action
  endTag() {
    this.state = 'SELECT_FIELD';
    this.maybeAddSearchTag();
  }

  @action
  cancelTag() {
    this.state = 'START';
    this.searchValue = '';
    this.maybeAddSearchTag();
  }

  @action
  removeTag(name: string) {
    delete this.controls[name];
    this.formControl.setFieldValue(name, undefined);
  }

  @action
  updateControls(fields: string[]) {
    for (const field of fields) {
      if (!(field in this.controls)) {
        this.controls[field] = {
          field: this.formControl.fields[field],
        };
      }
    }

    for (const field of Object.keys(this.controls)) {
      if (!fields.includes(field)) {
        delete this.controls[field];
      }
    }
  }
}

export interface TagSearchUseFieldResult {
  value: any;
  errorMessages: readonly string[];
}

/**
 * Implement this interface to handle formik & mobx-zod-form at the same time.
 */
export interface TagSearchInputFormControl {
  setFieldValue(name: string, value: any): void;
  getFieldValue(name: string): any;
  fields: Record<string, TagSearchField>;
  useField(name: string): TagSearchUseFieldResult;
  useControlFields?: () => string[];
  getSchema?: (name: string) => ZodTypeAny;
}

export class TagSearchInputFormikControl implements TagSearchInputFormControl {
  constructor(
    public fields: Record<string, TagSearchField>,
    public formikContext: FormikContextType<any>,
  ) {}

  setFieldValue(name: string, value: any) {
    this.formikContext.setFieldValue(name, value);
  }

  getFieldValue(name: string): any {
    return this.formikContext.getFieldProps(name).value;
  }

  useField(name: string): TagSearchUseFieldResult {
    const [field, meta] = useField(name);

    return {
      value: field.value,
      errorMessages: meta.error ? [meta.error] : [],
    };
  }
}

export class TagSearchInputMobxZodControl implements TagSearchInputFormControl {
  fields: Record<string, TagSearchField>;

  constructor(
    public mobxZodFields: Record<string, MobxZodField<ZodTypeAny>>,
    public form: ReactForm<any>,
    public fieldsInputOptions?: Record<string, InputOptions>,
  ) {
    this.fields = Object.fromEntries(
      Object.entries(mobxZodFields).map(([name, field]) => [
        name,
        {
          label: field.type.getFormMeta().label ?? name,
          inputOptions: fieldsInputOptions?.[name],
        },
      ]),
    );
  }

  setFieldValue(name: string, value: any): void {
    this.mobxZodFields[name].setOutput(value);
  }

  getFieldValue(name: string): any {
    const result = this.mobxZodFields[name].decodeResult;
    return result.success ? result.data : result.input;
  }

  useField(name: string): TagSearchUseFieldResult {
    const field = this.mobxZodFields[name];
    return {
      value: this.getFieldValue(name),
      errorMessages: field.errorMessages,
    };
  }

  useControlFields = (): string[] => {
    return Object.entries(this.mobxZodFields).flatMap(([name, field]) => {
      if (getDecodeResult(field.decodeResult)) {
        return [name];
      } else {
        return [];
      }
    });
  };

  getSchema = (name: string): ZodTypeAny => {
    return this.mobxZodFields[name].type;
  };
}
